// src/APIKeysSettings.js
import React, { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const APIKeysSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile());
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [connectToast, setConnectToast] = useState(false);
  const [connectErrorToast, setConnectErrorToast] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile());
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Check redirect result from Stripe OAuth callback
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === '1') {
      setConnectToast(true);
      setTimeout(() => setConnectToast(false), 5000);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('stripe_error')) {
      setConnectErrorToast(true);
      setTimeout(() => setConnectErrorToast(false), 7000);
      window.history.replaceState({}, '', window.location.pathname);
    }
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/stripe/connect/status`, {
        headers: { ...getTenantHeaders() }
      });
      if (!res.ok) throw new Error(`Status check failed (${res.status})`);
      const data = await res.json();
      setConnected(!!data.connected);
      setStripeAccountId(data.stripe_account_id || null);
    } catch (error) {
      console.error('Failed to load Connect status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/stripe/connect/authorize`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...getTenantHeaders() }
      });
      if (!res.ok) throw new Error(`Authorize failed (${res.status})`);
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Failed to start Connect flow:', error);
      alert('Unable to start Stripe Connect. Please try again.');
    }
  };

  const maskAccountId = (id) => {
    if (!id || id.length < 8) return id;
    return id.slice(0, 8) + '...' + id.slice(-4);
  };

  const styles = {
    page: { padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'), backgroundColor: '#E8E2DA', minHeight: '100vh' },
    contentWrapper: { maxWidth: '1200px', padding: isPhone ? '0' : '0 12px', margin: '0 auto' },
    panel: { backgroundColor: '#fff7ed', border: isMobile ? '6px solid black' : '10px solid black', padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '40px'), marginBottom: '18px' },
    sectionTitleFirst: { fontWeight: 900, textTransform: 'uppercase', fontSize: '18px', margin: '0 0 14px 0' },
    instructionBox: { fontWeight: 900, fontSize: '13px', padding: '12px', border: '3px solid black', backgroundColor: '#e0f2fe', marginBottom: '20px', lineHeight: 1.8 },
    loadingBox: { padding: '30px', textAlign: 'center', fontWeight: 900, fontSize: '16px' },
    connectBtn: { background: '#635bff', color: '#fff', border: '5px solid black', padding: '14px 28px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase', fontSize: '16px', width: isMobile ? '100%' : 'auto' },
    connectedBadge: { display: 'inline-block', background: '#166534', color: '#fff', fontWeight: 900, fontSize: '15px', padding: '8px 18px', border: '4px solid black', marginBottom: '14px' },
    accountIdRow: { fontWeight: 900, fontSize: '14px', marginBottom: '10px', fontFamily: 'monospace' },
    connectedNote: { fontWeight: 700, fontSize: '13px', color: '#166534', lineHeight: 1.8 },
    toastBox: { fontWeight: 900, fontSize: '14px', padding: '12px', border: '3px solid black', backgroundColor: '#dcfce7', marginBottom: '14px', color: '#166534' },
    errorToastBox: { fontWeight: 900, fontSize: '14px', padding: '12px', border: '3px solid black', backgroundColor: '#fee2e2', marginBottom: '14px', color: '#991b1b' },
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-api-pink:hover { background-color: #FFC107 !important; color: #000 !important; border-color: #FFA500 !important; transition: all 0.12s ease; }
        .jf-api-pink:active { transform: translateY(1px); }
        .jf-connect-btn:hover { background-color: #4f46e5 !important; transition: all 0.12s ease; }
        .jf-connect-btn:active { transform: translateY(1px); }
      `}</style>

      <div style={styles.contentWrapper}>
        <TitleBar
          icon="💳"
          title="SET UP PAYMENTS"
          accent="#E8761D"
          right={
            <button
              className="jf-api-pink"
              onClick={() => navigate('/settings')}
              style={{ background: 'black', color: '#FFA500', border: '5px solid #E8761D', padding: '14px', fontWeight: 900, cursor: 'pointer', textTransform: 'uppercase' }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING PAYMENT SETTINGS...</div>
          ) : (
            <>
              <h2 style={styles.sectionTitleFirst}>CONNECT YOUR STRIPE ACCOUNT</h2>

              {connectToast && (
                <div style={styles.toastBox}>
                  ✅ Stripe account connected successfully! Your customers can now pay invoices online.
                </div>
              )}

              {connectErrorToast && (
                <div style={styles.errorToastBox}>
                  Stripe connection failed. Please try again.
                </div>
              )}

              {!connected ? (
                <>
                  <div style={styles.instructionBox}>
                    MyTool NOW uses Stripe Connect to process payments on your behalf. Click the button below to connect your Stripe account in 60 seconds — no API keys to copy.
                  </div>
                  <button
                    className="jf-connect-btn"
                    style={styles.connectBtn}
                    onClick={handleConnect}
                  >
                    🔗 CONNECT WITH STRIPE
                  </button>
                </>
              ) : (
                <>
                  <div style={styles.connectedBadge}>✅ STRIPE CONNECTED</div>
                  <div style={styles.accountIdRow}>Account: {maskAccountId(stripeAccountId)}</div>
                  <div style={styles.connectedNote}>
                    Payments are live. Your customers can pay invoices online.
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default APIKeysSettings;
